import React from "react";
import { useParams } from "react-router-dom";
import TeamLogo from "./TeamLogo";
import LatestResult from "./LatestResult";
import PreviousResult from "./PreviousResult";
import LatestResultDetail from "./LatestResultDetail";
import TableRow from "./TableRow";
import { getPrettyDate } from "../lib/format";
import { useApi } from "../lib/useApi";

// The competition line under the team names. South African competition names
// could be shown better here.
const resolve_comp = (comp_country, game_week, competition) => {
  if (comp_country !== "" && game_week !== "") {
    return `${competition} (${comp_country}), ${game_week}`;
  } else if (comp_country !== "") {
    return `${competition} (${comp_country})`;
  } else if (game_week !== "") {
    return `${competition} Game Week ${game_week}`;
  } else {
    return `${competition}`;
  }
};

const pretty_kickoff = (kick_off_time, program_date) => {
  if (kick_off_time.includes(" ")) {
    return getPrettyDate(kick_off_time.substring(0, 10)) + ", " + kick_off_time.substring(11);
  } else {
    return getPrettyDate(program_date) + ", " + kick_off_time;
  }
};

// The response describes the match from the home side; switch_teams 1 means the
// pool lists the teams the other way round.
const switchTeams = (temp) => {
  if (temp.switch_teams !== 1) return temp;
  return {
    ...temp,
    team_a_name: temp.team_b_name,
    team_b_name: temp.team_a_name,
    team_a_id: temp.team_b_id,
    team_b_id: temp.team_a_id,
    team_a_image_id: temp.team_b_image_id,
    team_b_image_id: temp.team_a_image_id,
    table_summary_a: temp.table_summary_b,
    table_summary_b: temp.table_summary_a,
    record_summary_a: temp.record_summary_b,
    record_summary_b: temp.record_summary_a,
    latest_results_team_a: temp.latest_results_team_b,
    latest_results_team_b: temp.latest_results_team_a,
    league_standings_a: temp.league_standings_b,
    league_standings_b: temp.league_standings_a,
  };
};

const TeamImage = ({ image_id, id, name }) => {
  if (!image_id) return <TeamLogo id={id} name={name} small={false} />;
  return (
    <img
      alt={name}
      src={`https://4rsoccer6us.blob.core.windows.net/soccer-team-logos/${image_id}.png`}
      style={{ float: "none", width: "150px", height: "150px" }}
    />
  );
};

const firstFive = (results, render) => results.map((result, index) => (index < 5 ? render(result) : null));

// TableRow draws a gap above a row whose position isn't after the previous row's.
const standingsRows = (league_standings, team_a_id, team_b_id) => {
  let previous = -1;
  return league_standings.map((table_row) => {
    const rowPrevious = previous;
    previous = parseInt(table_row.position);
    return <TableRow item={table_row} previous={rowPrevious} team_a_id={team_a_id} team_b_id={team_b_id} />;
  });
};

const Standings = ({ standings, team_a_id, team_b_id }) => (
  <div className="row">
    <div className="large-12 columns">
      <table style={{ width: "100%", borderSpacing: "0px" }}>
        <thead>
          <tr>
            <td colSpan={10} style={{ textAlign: "center" }}>
              {standings.league_title} Standings
            </td>
          </tr>
          <tr>
            <td> </td>
            <td className="hideForSmall">&nbsp;</td>
            <td> </td>
            <td className="centertext">P</td>
            <td className="centertext">W</td>
            <td className="centertext">D</td>
            <td className="centertext">L</td>
            <td className="hideForSmall centertext">GF</td>
            <td className="hideForSmall centertext">GA</td>
            <td className="centertext">PTS</td>
          </tr>
        </thead>
        <tbody>{standingsRows(standings.league_standings, team_a_id, team_b_id)}</tbody>
      </table>
    </div>
  </div>
);

// Foundation gives tbody td this line-height but not th, so the row headers set it
// themselves to look as they did when they were td.
const rowHeaderStyle = { lineHeight: "1.125em" };

const H2HDetails = ({ H2H }) => (
  <>
    <div className="row">
      <h3 style={{ textAlign: "center", margin: "3px" }}>
        <span style={{ color: "seagreen" }}>{H2H.team_a_name}</span> v <span style={{ color: "royalblue" }}>{H2H.team_b_name}</span>
      </h3>
      <h5 style={{ textAlign: "center", margin: "3px" }}>{resolve_comp(H2H.comp_country, H2H.game_week, H2H.competition)}</h5>
      {H2H.aggregate !== "" ? <h6 style={{ textAlign: "center", margin: "3px" }}>Aggregate: {H2H.aggregate}</h6> : null}
      {H2H.venue !== "" ? (
        <h6 style={{ textAlign: "center", margin: "3px" }}>
          {H2H.venue}, {pretty_kickoff(H2H.kick_off_time, H2H.program_date)}
        </h6>
      ) : (
        <h6 style={{ textAlign: "center", margin: "3px" }}>{pretty_kickoff(H2H.kick_off_time, H2H.program_date)}</h6>
      )}
      <div className="small-6 columns">
        <center>
          <TeamImage image_id={H2H.team_a_image_id} id={H2H.team_a_id} name={H2H.team_a_name} />
        </center>
      </div>
      <div className="small-6 columns">
        <center>
          <TeamImage image_id={H2H.team_b_image_id} id={H2H.team_b_id} name={H2H.team_b_name} />
        </center>
      </div>
    </div>
    <br />
    <div className="row">
      <div className="large-12 columns">
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th scope="col" colSpan="2">In Short</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="2">
                {H2H.preview_250}{" "}
                {H2H.full_preview ? (
                  <>
                    <br />
                    <a href={`/FullPreview/${H2H.full_preview}`} style={{ color: "#001489", fontWeight: "bold" }}>
                      Read Full Match Preview
                    </a>
                  </>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div className="row">
      <div className="large-12 columns">
        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td style={{ backgroundColor: "seagreen", color: "white", fontSize: "0.875em" }}>{H2H.table_summary_a}</td>
              <th scope="row" className="centertext" style={rowHeaderStyle}>
                <strong>Log (Pts) F-A</strong>
              </th>
              <td style={{ backgroundColor: "royalblue", color: "white", fontSize: "0.875em", textAlign: "right" }}>{H2H.table_summary_b}</td>
            </tr>
            <tr>
              <td style={{ backgroundColor: "seagreen", color: "white", fontSize: "0.875em" }}>{H2H.record_summary_a}</td>
              <th scope="row" className="centertext" style={rowHeaderStyle}>
                <strong>P-W-D-L</strong>
              </th>
              <td style={{ backgroundColor: "royalblue", color: "white", fontSize: "0.875em", textAlign: "right" }}>{H2H.record_summary_b}</td>
            </tr>
            <tr>
              <td style={{ lineHeight: "1.6em" }}>{firstFive(H2H.latest_results_team_a, (result) => <LatestResult item={result} />)}</td>
              <th scope="row" className="centertext" style={rowHeaderStyle}>
                <strong>Form last 5</strong>
              </th>
              <td style={{ lineHeight: "1.6em" }} className="righttext">
                {firstFive(H2H.latest_results_team_b, (result) => <LatestResult item={result} />)}
              </td>
            </tr>
            <tr>
              <td style={{ lineHeight: "1.6em" }}>
                {firstFive(
                  H2H.latest_results_team_a.filter((result) => result.match_home_or_away === "H"),
                  (result) => <LatestResult item={result} />
                )}
              </td>
              <th scope="row" className="centertext" style={rowHeaderStyle}>
                <strong>
                  Last 5 (<span style={{ color: "seagreen", fontWeight: "bolder" }}>Home</span>/
                  <span style={{ color: "royalblue", fontWeight: "bolder" }}>Away</span>)
                </strong>
              </th>
              <td style={{ lineHeight: "1.6em" }} className="righttext">
                {firstFive(
                  H2H.latest_results_team_b.filter((result) => result.match_home_or_away === "A"),
                  (result) => <LatestResult item={result} />
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div className="row">
      <div className="large-12 columns">
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              {H2H.head_2_head_results && H2H.head_2_head_results.length === 0 ? (
                <td colSpan={5}>Previous Meetings: None</td>
              ) : (
                <td colSpan={5}>Previous Meetings</td>
              )}
            </tr>
          </thead>
          <tbody>
            {H2H.head_2_head_results &&
              firstFive(H2H.head_2_head_results, (result) => <PreviousResult item={result} team_a_id={H2H.team_a_id} team_b_id={H2H.team_b_id} />)}
          </tbody>
        </table>
      </div>
    </div>
    <div className="row">
      <div className="large-6 columns">
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <td colSpan={5} style={{ backgroundColor: "seagreen", color: "white", fontSize: "0.875em" }}>
                {H2H.team_a_name} Latest Results:
              </td>
            </tr>
          </thead>
          <tbody>{firstFive(H2H.latest_results_team_a, (result) => <LatestResultDetail item={result} />)}</tbody>
        </table>
      </div>
      <div className="large-6 columns">
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <td colSpan={5} style={{ backgroundColor: "royalblue", color: "white", fontSize: "0.875em" }}>
                {H2H.team_b_name} Latest Results:
              </td>
            </tr>
          </thead>
          <tbody>{firstFive(H2H.latest_results_team_b, (result) => <LatestResultDetail item={result} />)}</tbody>
        </table>
      </div>
    </div>
    {H2H.league_standings_a ? <Standings standings={H2H.league_standings_a} team_a_id={H2H.team_a_id} team_b_id={H2H.team_b_id} /> : null}
    {H2H.league_standings_b ? <Standings standings={H2H.league_standings_b} team_a_id={H2H.team_a_id} team_b_id={H2H.team_b_id} /> : null}{" "}
  </>
);

const H2HPage = () => {
  const { id } = useParams();
  const { data: H2H = {} } = useApi(`/api/GetH2H/${id}`, { select: switchTeams });

  return <>{H2H.kick_off_time ? <H2HDetails H2H={H2H} /> : <div>Loading...</div>}</>;
};

export default H2HPage;
